import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

type ModeValue = 'clinical' | 'admin';

interface IModeOption {
  key: string;
  value: ModeValue;
}

@Component({
  standalone: false,
  selector: 'app-mode-switcher',
  templateUrl: './mode-switcher.component.html',
  styleUrls: ['./mode-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModeSwitcherComponent {
  readonly modeOptions: IModeOption[] = [
    { key: 'shared.components.sidebar.mode_chip.clinical', value: 'clinical' },
    { key: 'shared.components.sidebar.mode_chip.admin', value: 'admin' },
  ];

  /** True when the current URL is `/admin` exactly or under `/admin/*`. */
  readonly isAdminMode$: Observable<boolean> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.isAdminUrl(this.router.url))
  );

  /** The mode of the currently-active segment in the toggle. */
  readonly selectedMode$: Observable<ModeValue> = this.isAdminMode$.pipe(
    map(isAdmin => (isAdmin ? 'admin' : 'clinical'))
  );

  constructor(private readonly router: Router) {}

  /**
   * @description Navigates to the chosen mode. No-op when the user is
   * already in the target mode (prevents the `/admin` redirect from
   * dropping the user from `/admin/customer-requests` back to the
   * `/admin/customers` default).
   * @param {ModeValue} mode target perspective
   */
  selectMode(mode: ModeValue): void {
    const targetIsAdmin = mode === 'admin';
    const currentlyAdmin = this.isAdminUrl(this.router.url);
    if (targetIsAdmin === currentlyAdmin) {
      return;
    }
    void this.router.navigate([targetIsAdmin ? '/admin' : '/home']);
  }

  /**
   * @description Strict `/admin` prefix match — true only for `/admin` itself
   * or routes under `/admin/`. Prevents `/administrative-*` style paths from
   * being misclassified as admin mode.
   * @param {string} url current router URL
   * @returns {boolean}
   */
  private isAdminUrl(url: string): boolean {
    return url === '/admin' || url.startsWith('/admin/');
  }
}
